import {
  getContrastColor,
  getTagColor,
} from "@taskfortress/core/src/utils/colors"
import React, { useMemo } from "react"
import { useData } from "../../providers/DataProvider"
import { cn } from "../../styles/utils"
import type { PropsWithClassname } from "../../types"
import { Box } from "../Box"
import { Pressable } from "../Pressable"
import { Text } from "../Text"

interface TagChipProps extends PropsWithClassname {
  tagName: string
  onPress?: () => void
  textClassName?: string
}

export const TagChip: React.FC<TagChipProps> = React.memo(
  ({ tagName, onPress, className, textClassName }) => {
    const { tags } = useData()

    // Memoize the tag lookup to avoid unnecessary array searches
    const tagObject = useMemo(
      () => tags.find((tag) => tag.name === tagName),
      [tags, tagName],
    )

    // Memoize color calculations to avoid unnecessary computations
    const { backgroundColor, textColor } = useMemo(() => {
      const bgColor = tagObject?.color || getTagColor(tagName)
      return {
        backgroundColor: bgColor,
        textColor: getContrastColor(bgColor),
      }
    }, [tagObject?.color, tagName])

    const content = (
      <Box
        className={cn("px-2 py-0.5 rounded-full", className)}
        style={{ backgroundColor }}
      >
        <Text
          className={cn("text-[10px]", textClassName)}
          style={{ color: textColor }}
        >
          {tagName}
        </Text>
      </Box>
    )

    if (onPress) {
      return (
        <Pressable onPress={onPress} className="rounded-full">
          {content}
        </Pressable>
      )
    }

    return content
  },
)
